/**
 * ImageUploadField - Reusable image upload component for admin pages.
 * Supports drag-drop, click-to-upload, preview of current/uploaded image,
 * and routes to the canonical CMS media authority
 * (`POST /api/admin/media/direct-upload`, backed by
 * `MediaStorageService.direct_upload_cms_media` — RBAC-gated by the
 * `content.manage` permission).
 *
 * TREATX-OWNER-UAT-MEDIA-UPLOAD-410-01 (2026-08): the previous
 * implementation posted to `/api/media/upload`, which
 * `server.py:2438` intentionally returns HTTP 410 for (retired
 * pre-M0 media path). The Owner hit this while uploading the Hair
 * Transplant category image. The retired path is now removed from
 * every active caller in this component; the canonical path stores
 * the asset in `cms_media_uploads` (SSOT) and returns
 * `{success, file_key, public_url, ...}`. We map the response to
 * the `{id, public_url}` shape existing callers already consume.
 */
import React, { useState, useRef, useCallback } from 'react';
import { Button } from './ui/button';
import { Progress } from './ui/progress';
import { Upload, X, Loader2, ImagePlus, Check } from 'lucide-react';
import api from '../utils/api';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const MAX_SIZE = 10 * 1024 * 1024; // 10MB

const compressImage = (file, maxDim = 1920, quality = 0.82) =>
  new Promise((resolve) => {
    if (!file.type.startsWith('image/')) return resolve(file);
    const img = new Image();
    img.onload = () => {
      let { width, height } = img;
      if (width <= maxDim && height <= maxDim && file.size < 2 * 1024 * 1024)
        return resolve(file);
      const scale = Math.min(maxDim / width, maxDim / height, 1);
      width = Math.round(width * scale);
      height = Math.round(height * scale);
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      canvas.getContext('2d').drawImage(img, 0, 0, width, height);
      canvas.toBlob(
        (blob) =>
          resolve(blob ? new File([blob], file.name, { type: 'image/jpeg' }) : file),
        'image/jpeg',
        quality
      );
    };
    img.onerror = () => resolve(file);
    img.src = URL.createObjectURL(file);
  });

/**
 * Single image uploader with preview.
 *
 * Props:
 *  - currentImage: string URL of the existing image (Unsplash or media pipeline)
 *  - mediaId: string|null current media asset ID
 *  - assetType: string ('treatment_image' | 'category_image')
 *  - onUploadComplete: (asset) => void  — called with {id, public_url, ...}
 *  - onClear: () => void
 */
export const ImageUploadField = ({
  currentImage,
  mediaId,
  assetType = 'treatment_image',
  onUploadComplete,
  onClear,
  label = 'Image',
}) => {
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [dragOver, setDragOver] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const fileRef = useRef(null);

  const resolveImageUrl = (url) => {
    if (!url) return null;
    if (url.startsWith('http')) return url;
    return `${API_URL}${url.startsWith('/') ? '' : '/'}${url}`;
  };

  const displayUrl = previewUrl || resolveImageUrl(currentImage);

  const handleFile = useCallback(
    async (file) => {
      if (!file || !file.type.startsWith('image/')) return;
      if (file.size > MAX_SIZE) {
        alert('File too large (max 10 MB)');
        return;
      }
      setUploading(true);
      setProgress(20);

      try {
        const compressed = await compressImage(file);
        setProgress(40);
        setPreviewUrl(URL.createObjectURL(compressed));

        const formData = new FormData();
        formData.append('file', compressed, compressed.name || 'upload.png');

        // Canonical CMS media authority — see the file-level docstring
        // for the retired /media/upload path this replaces. Axios
        // detects the FormData payload and sets the multipart
        // boundary automatically; we explicitly set the header so
        // any global JSON interceptor cannot override it.
        const res = await api.post(`/admin/media/direct-upload`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        setProgress(80);
        const data = res.data;

        if (data.success && data.public_url) {
          setProgress(100);
          // Map the canonical CMS response shape to the {id, public_url}
          // contract existing consumers (CategoryManagement,
          // TreatmentManagement, TreatXAdminDashboard) already expect.
          onUploadComplete?.({
            id: data.file_key,
            public_url: data.public_url,
            file_key: data.file_key,
            file_name: data.file_name,
            content_type: data.content_type,
            file_size: data.file_size,
          });
        } else {
          throw new Error(data.error || data.detail || 'Upload failed');
        }
      } catch (err) {
        console.error('[ImageUploadField] upload error:', err);
        // TREATX-OWNER-UAT-MEDIA-UPLOAD-410-01 — failed upload MUST NOT
        // destroy the existing image reference. We clear only the
        // transient preview (not the persisted `currentImage`) and
        // surface the server error to the caller.
        setPreviewUrl(null);
        const msg = err?.response?.data?.error
          || err?.response?.data?.detail
          || err?.message
          || 'Unknown error';
        alert('Upload failed: ' + msg);
      } finally {
        setTimeout(() => {
          setUploading(false);
          setProgress(0);
        }, 600);
      }
    },
    [assetType, onUploadComplete]
  );

  const onDrop = useCallback(
    (e) => {
      e.preventDefault();
      setDragOver(false);
      const file = e.dataTransfer?.files?.[0];
      if (file) handleFile(file);
    },
    [handleFile]
  );

  const handleClear = () => {
    setPreviewUrl(null);
    onClear?.();
  };

  return (
    <div data-testid="image-upload-field" className="w-full">
      {displayUrl ? (
        <div className="relative rounded-lg overflow-hidden border border-slate-200 bg-slate-50">
          <img
            data-testid="image-upload-preview"
            src={displayUrl}
            alt={label}
            className="w-full h-48 object-cover"
            onError={(e) => { e.target.src = ''; e.target.alt = 'Image failed to load'; }}
          />
          {mediaId && (
            <span className="absolute top-2 left-2 bg-emerald-600 text-white text-xs px-2 py-0.5 rounded">
              <Check className="inline h-3 w-3 mr-1" />Uploaded via pipeline
            </span>
          )}
          <div className="absolute top-2 right-2 flex gap-1">
            <Button
              type="button"
              size="sm"
              variant="secondary"
              className="h-7 px-2 text-xs bg-white/90 hover:bg-white"
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
              data-testid="image-upload-replace-btn"
            >
              <Upload className="h-3 w-3 mr-1" />Replace
            </Button>
            <Button
              type="button"
              size="sm"
              variant="destructive"
              className="h-7 px-2 text-xs"
              onClick={handleClear}
              disabled={uploading}
              data-testid="image-upload-clear-btn"
            >
              <X className="h-3 w-3" />
            </Button>
          </div>
          {uploading && (
            <div className="absolute bottom-0 left-0 right-0 p-2 bg-black/40">
              <Progress value={progress} className="h-1.5" />
            </div>
          )}
        </div>
      ) : (
        <div
          data-testid="image-upload-dropzone"
          className={`relative rounded-lg border-2 border-dashed p-8 text-center transition-colors cursor-pointer
            ${dragOver ? 'border-blue-500 bg-blue-50' : 'border-slate-300 bg-slate-50 hover:border-slate-400'}`}
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={onDrop}
          onClick={() => !uploading && fileRef.current?.click()}
        >
          {uploading ? (
            <div className="space-y-2">
              <Loader2 className="h-8 w-8 mx-auto text-blue-500 animate-spin" />
              <p className="text-sm text-slate-600">Uploading...</p>
              <Progress value={progress} className="h-1.5 max-w-[200px] mx-auto" />
            </div>
          ) : (
            <div className="space-y-2">
              <ImagePlus className="h-8 w-8 mx-auto text-slate-400" />
              <p className="text-sm font-medium text-slate-600">Drag & drop an image here</p>
              <p className="text-xs text-slate-400">or click to browse (JPEG, PNG, WebP up to 10 MB)</p>
            </div>
          )}
        </div>
      )}
      <input
        ref={fileRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) handleFile(f);
          e.target.value = '';
        }}
      />
    </div>
  );
};

/**
 * Gallery uploader — multiple images with cover selection.
 *
 * Props:
 *  - images: Array<{url, media_id?}>
 *  - coverImage: string URL of the current cover
 *  - onImagesChange: (images) => void
 *  - onSetCover: (url) => void
 */
export const GalleryUploader = ({
  images = [],
  coverImage,
  onImagesChange,
  onSetCover,
  assetType = 'treatment_image',
}) => {
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const handleFile = useCallback(
    async (file) => {
      if (!file || !file.type.startsWith('image/')) return;
      setUploading(true);
      try {
        const compressed = await compressImage(file);
        const formData = new FormData();
        formData.append('file', compressed, compressed.name || 'upload.png');

        // TREATX-OWNER-UAT-MEDIA-UPLOAD-410-01: canonical CMS media
        // authority — same replacement rationale as the single-image
        // uploader above.
        const res = await api.post(`/admin/media/direct-upload`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        const data = res.data;
        if (data.success && data.public_url) {
          const newImg = { url: data.public_url, media_id: data.file_key };
          onImagesChange?.([...images, newImg]);
        }
      } catch (err) {
        console.error('[GalleryUploader]', err);
      } finally {
        setUploading(false);
      }
    },
    [images, assetType, onImagesChange]
  );

  const removeImage = (idx) => {
    const updated = images.filter((_, i) => i !== idx);
    onImagesChange?.(updated);
  };

  return (
    <div data-testid="gallery-uploader" className="space-y-3">
      <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
        {images.map((img, i) => {
          const imgUrl = img.url?.startsWith('http') ? img.url : `${API_URL}${img.url}`;
          const isCover = coverImage === img.url || coverImage === imgUrl;
          return (
            <div key={i} className="relative group rounded-lg overflow-hidden border border-slate-200">
              <img
                src={imgUrl}
                alt={`Gallery ${i + 1}`}
                className={`w-full h-24 object-cover cursor-pointer ${isCover ? 'ring-2 ring-blue-500' : ''}`}
                onClick={() => onSetCover?.(img.url)}
              />
              <button
                type="button"
                onClick={() => removeImage(i)}
                className="absolute top-1 right-1 bg-red-500 text-white p-0.5 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
              >
                <X className="h-3 w-3" />
              </button>
              {isCover && (
                <span className="absolute bottom-1 left-1 bg-blue-600 text-white text-[10px] px-1.5 py-0.5 rounded">
                  Cover
                </span>
              )}
            </div>
          );
        })}

        {/* Add button */}
        <button
          type="button"
          className="h-24 rounded-lg border-2 border-dashed border-slate-300 flex flex-col items-center justify-center text-slate-400 hover:border-slate-400 hover:text-slate-500 transition-colors"
          onClick={() => !uploading && fileRef.current?.click()}
          disabled={uploading}
          data-testid="gallery-add-btn"
        >
          {uploading ? <Loader2 className="h-5 w-5 animate-spin" /> : <ImagePlus className="h-5 w-5" />}
          <span className="text-[10px] mt-1">{uploading ? 'Uploading...' : 'Add'}</span>
        </button>
      </div>
      <input
        ref={fileRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) handleFile(f);
          e.target.value = '';
        }}
      />
    </div>
  );
};
